// Section 13 and 18: detected crimes become justice cases. The justice policy's response applies
// at once, unless "ask each case" is on: then a JusticePrompt goes into `openPrompts` with the
// proposed response and the responses available now, and the case waits `pending`. The next
// Decision answers it in `eventChoices` ({ kind: 'justice', response }); the answer is applied at
// the start of the next crime phase (E1-08). A response that has become unavailable (the jail
// filled up) falls back to the proposal, then to restitution, which is always available.
import { mintId } from '../../core/ids';
import { JUSTICE_RESPONSES, responseBlock } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import type {
  CaseId,
  CrimeKind,
  JusticePrompt,
  JusticeResponse,
  PersonId,
  PhaseContext,
  Prompt,
} from '../../core/types';
import { applyResponse } from './justice';
import type { CrimeRun } from './trust';

/** Record a detected crime as a pending case; earlier cases of the offender are marked reoffended. */
export function openCase(
  ctx: PhaseContext,
  crime: CrimeKind,
  offenderId: PersonId,
  victimIds: readonly PersonId[],
): CaseId {
  const d = ctx.draft;
  for (const c of sortedValues(d.cases)) if (c.offenderId === offenderId) c.reoffended = true;
  const id = mintId(d, 'case');
  d.cases[id] = {
    id,
    turn: ctx.season.turn,
    crime,
    offenderId,
    victimIds: [...victimIds],
    response: null,
    status: 'pending',
    reoffended: false,
  };
  const offender = d.people[offenderId];
  if (offender !== undefined) offender.offences += 1;
  return id;
}

/** The policy's response if it can be used now, else restitution (always available). */
export function proposedResponse(ctx: PhaseContext, response: JusticeResponse): JusticeResponse {
  return responseBlock(ctx.draft, ctx.cfg, response) === null ? response : 'restitution';
}

export function justicePromptId(caseId: CaseId): string {
  return `justice:${caseId}`;
}

/** Apply the justice policy to a new case, or ask the player (ask each case). */
export function judge(ctx: PhaseContext, run: CrimeRun, caseId: CaseId): void {
  const c = ctx.draft.cases[caseId];
  if (c === undefined) return;
  const policy = ctx.state.policies.justice;
  const proposed = proposedResponse(ctx, policy.response);
  if (!policy.askEachCase) {
    applyResponse(ctx, run, caseId, proposed);
    return;
  }
  const prompt: JusticePrompt = {
    kind: 'justice',
    id: justicePromptId(caseId),
    turn: ctx.season.turn,
    caseId,
    offenderId: c.offenderId,
    crime: c.crime,
    proposed,
    options: JUSTICE_RESPONSES.filter((r) => responseBlock(ctx.draft, ctx.cfg, r) === null),
  };
  ctx.draft.openPrompts.push(prompt);
}

function usable(ctx: PhaseContext, prompt: JusticePrompt, response: JusticeResponse): boolean {
  // Execution was unlocked when the player chose it from the prompt's options (Section 18).
  if (response === 'execution' && prompt.options.includes('execution')) return true;
  return responseBlock(ctx.draft, ctx.cfg, response) === null;
}

/** Apply last season's answered justice prompts and close them; unanswered ones stay open. */
export function resolveAnswered(ctx: PhaseContext, run: CrimeRun): void {
  const remaining: Prompt[] = [];
  const answered: { prompt: JusticePrompt; response: JusticeResponse }[] = [];
  for (const prompt of ctx.draft.openPrompts) {
    const answer = ctx.answers[prompt.id];
    if (prompt.kind !== 'justice' || answer?.kind !== 'justice') {
      remaining.push(prompt);
      continue;
    }
    answered.push({ prompt, response: answer.response });
  }
  ctx.draft.openPrompts = remaining;
  for (const { prompt, response } of answered) {
    const chosen = [response, prompt.proposed].find((r) => usable(ctx, prompt, r)) ?? 'restitution';
    applyResponse(ctx, run, prompt.caseId, chosen);
  }
}
